import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import moment from 'moment';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowUturnLeftIcon } from '@heroicons/react/24/outline';
import { CompanyApi } from '../../../../../api/companyApi';
import {
  errorToast,
  successToast,
  getLocalStorageItem,
  isCopanySubAdmin
} from '../../../../../utils/helper';
import Loader from '../../../../../component/common/Loader';
import Topbar from '../../../../PulseSurvey/Topbar';
import Breadcrumb from '../../../../../component/common/Breadcrumb';
import { StandardDropdown } from '../../../../../component/common/StandardUI';
import ConfirmPopup from '../../../../../component/common/modals/ConfirmPopup';
import { CheckMarkIcon, RecycleBinIcon } from '../components/icons';
import pencilIcon from '../../../../../assets/svg/icons/Pencil--Streamline-Flex.svg';

const ViewRoadMap = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const userData = JSON.parse(getLocalStorageItem('userData'));
  const slug = userData?.slug;
  const isSubAdmin = isCopanySubAdmin();

  const [roadMap, setRoadMap] = useState(null);
  const [loading, setLoading] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [completingItemId, setCompletingItemId] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const fetchRoadMap = async () => {
    try {
      setLoading(true);
      const response = await CompanyApi.getRoadMapById(id);
      if (response?.data?.meta?.code === 1) {
        setRoadMap(response?.data?.data);
      } else {
        errorToast(response?.data?.meta?.message || 'Failed to load road map');
      }
    } catch (error) {
      errorToast(error.message || 'Failed to load road map');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRoadMap();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const runAction = async (apiCall, fallbackError, setBusy) => {
    try {
      setBusy(true);
      const response = await apiCall();
      if (response?.data?.meta?.code === 1) {
        successToast(response?.data?.meta?.message || 'Road map updated');
        await fetchRoadMap();
        return true;
      }
      errorToast(response?.data?.meta?.message || fallbackError);
      return false;
    } catch (error) {
      errorToast(error.message || fallbackError);
      return false;
    } finally {
      setBusy(false);
    }
  };

  const handleMarkAsCompleted = () =>
    runAction(
      () => CompanyApi.markRoadMapAsCompleted(id),
      'Failed to update road map',
      setCompleting
    );

  const handleMarkAsIncomplete = () =>
    runAction(
      () => CompanyApi.markRoadMapAsIncomplete(id),
      'Failed to reopen road map',
      setCompleting
    );

  const handleCompleteItem = (itemId) =>
    runAction(
      () => CompanyApi.markRoadMapItemCompleted(id, itemId),
      'Failed to complete column',
      (busy) => setCompletingItemId(busy ? itemId : null)
    );

  const handleDelete = async () => {
    try {
      setDeleting(true);
      const response = await CompanyApi.deleteRoadMap(id);
      if (response?.data?.meta?.code === 1) {
        successToast(response?.data?.meta?.message || 'Road map deleted');
        navigate(`/${slug}/people-plan/roadmaps`);
      } else {
        errorToast(response?.data?.meta?.message || 'Failed to delete road map');
      }
    } catch (error) {
      errorToast(error.message || 'Failed to delete road map');
    } finally {
      setDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  const pages = [
    { name: 'People plan', href: `/${slug}/people-plan`, current: false },
    { name: 'Road Maps', href: `/${slug}/people-plan/roadmaps`, current: false },
    { name: roadMap?.title || 'Road Map', href: '#', current: true }
  ];

  if (loading && !roadMap) {
    return (
      <div className="relative lg:mb-[45px]">
        <Loader />
      </div>
    );
  }

  if (!roadMap) return null;

  const isCompleted = roadMap.status === 'completed';
  const isDraft = roadMap.status === 'draft';
  const items = Array.isArray(roadMap.items) ? roadMap.items : [];

  // draft     → Edit, Delete
  // active    → Edit, Complete, Delete
  // completed → Incomplete, Delete
  const actionOptions = [
    ...(isCompleted
      ? [
        {
          label: 'Mark as incomplete',
          value: 'incomplete',
          icon: <ArrowUturnLeftIcon className="w-5 h-5" />
        }
      ]
      : [
        {
          label: 'Edit road map',
          value: 'edit',
          icon: <img src={pencilIcon} alt="" className="w-5 h-5 dark:invert" />
        }
      ]),
    ...(!isCompleted && !isDraft
      ? [
        {
          label: 'Mark as complete',
          value: 'complete',
          icon: <CheckMarkIcon className="w-5 h-5" />
        }
      ]
      : []),
    {
      label: 'Delete road map',
      value: 'delete',
      icon: <RecycleBinIcon className="w-5 h-5" />,
      className: 'text-red-500 hover:text-red-600'
    }
  ];

  const handleAction = (action) => {
    if (action === 'edit') navigate(`/${slug}/people-plan/roadmaps/edit/${id}`);
    else if (action === 'complete') handleMarkAsCompleted();
    else if (action === 'incomplete') handleMarkAsIncomplete();
    else if (action === 'delete') setShowDeleteConfirm(true);
  };

  return (
    <div className="relative lg:mb-[45px]">
      {(loading || completing || deleting || completingItemId) && <Loader />}
      <Helmet>
        <meta charSet="utf-8" />
        <title>Roadmap</title>
      </Helmet>
      <Topbar title="Roadmap" infoBtn={false} />
      <Breadcrumb pageList={pages} className="lg:mt-8" />

      <div className="mt-6">
        <div className="flex justify-between items-start mb-6">
          <div className="flex items-center gap-3">


          </div>
          <div className="flex flex-wrap gap-2 justify-end">

            {!isSubAdmin && (
              <StandardDropdown
                label="Actions"
                align="right"
                options={actionOptions}
                onChange={handleAction}
              />
            )}
          </div>
        </div>

        <div className="rounded-input border-[1.5px] border-shoorah-borderGray bg-white dark:bg-shoorah-darkBgTabColor dark:border-shoorah-darkBorder p-6 space-y-6">
          <div>
            <p className="text-lg font-medium text-gray-700 dark:text-gray-200">{roadMap.title}</p>
            <p className="text-sm text-gray-400 mt-1 whitespace-pre-wrap">
              {roadMap.description || '—'}
            </p>
          </div>

          {items.length > 0 ? (
            <div className="border-t border-shoorah-borderGray dark:border-shoorah-darkBorder pt-5">

              {/* items-stretch + flex-1 on the description keeps every column the
                  same height, so the footers line up however long the text is */}
              <div className="flex items-stretch gap-4 overflow-x-auto pb-2">
                {items.map((col, index) => {
                  const itemCompleted = col.status === 'completed';
                  const showCompleteButton = !isSubAdmin && !isDraft && col._id;
                  return (
                    <div key={col._id || index} className="w-[240px] shrink-0 flex flex-col gap-3">
                      <p className="w-full rounded-input border border-gray-300 dark:border-gray-600 px-4 py-2 text-sm text-center dark:text-gray-200">
                        {col.period || '—'}
                      </p>
                      <div className="rounded-input bg-shoorah-normanGray dark:bg-shoorah-darkBgColor p-3">
                        <p className="text-sm font-medium dark:text-white">{col.heading || '—'}</p>
                      </div>
                      <p className="flex-1 rounded-input bg-shoorah-normanGray dark:bg-shoorah-darkBgColor p-3 text-sm whitespace-pre-wrap dark:text-gray-200 min-h-[100px]">
                        {col.description || '—'}
                      </p>
                      {itemCompleted ? (
                        <span className="flex items-center justify-center gap-1 rounded-input border border-green-500 bg-green-100 text-green-700 text-xs font-medium py-2">
                          <CheckMarkIcon className="w-3.5 h-3.5" />
                          Completed
                        </span>
                      ) : (
                        showCompleteButton && (
                          <button
                            type="button"
                            onClick={() => handleCompleteItem(col._id)}
                            disabled={completingItemId === col._id}
                            className="w-full rounded-input border border-gray-300 dark:border-gray-600 py-2 text-xs text-gray-500 dark:text-gray-300 hover:border-shoorah-btn dark:hover:border-shoorah-btnDark disabled:opacity-50"
                          >
                            Mark complete
                          </button>
                        )
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ) : (
            <div className="border-t border-shoorah-borderGray dark:border-shoorah-darkBorder pt-5">
              <dt className="text-gray-400 text-sm">Date</dt>
              <dd className="text-gray-700 dark:text-gray-200 text-sm">
                {roadMap.date ? moment(roadMap.date).format('DD MMM YYYY') : '—'}
              </dd>
            </div>
          )}
        </div>
      </div>

      <ConfirmPopup
        open={showDeleteConfirm}
        setOpen={setShowDeleteConfirm}
        setAccepted={handleDelete}
        handleNo={() => setShowDeleteConfirm(false)}
        message={`Are you sure you want to delete "${roadMap.title}"? This action cannot be undone.`}
        isLoading={deleting}
      />
    </div>
  );
};

export default ViewRoadMap;
